import React from 'react';
import { CompleteKundali } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { VIMSHOTTARI_YEARS } from '../../utils/dashaCalculator';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';

export const Grantha4_MultiDasha: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails, dashas = [], panchang, planets = [], houses = [], charaKarakas = [], charaDasha } = kundali;

  // Auxiliary applicability evaluations
  const isKrishnaPaksha = Boolean(panchang?.paksha?.toLowerCase().includes('krishna'));
  const birthHour = parseInt(birthDetails.time?.split(':')[0] || '12', 10);
  const isDayBirth = birthHour >= 6 && birthHour < 18;
  const isShodashottariApplicable = (isDayBirth && isKrishnaPaksha) || (!isDayBirth && !isKrishnaPaksha);

  const rahuPl = planets.find(p => p.name === 'Rahu');
  const ascLord = houses[0]?.signLord;
  const lagnaLordPl = planets.find(p => p.name === ascLord);
  const isAshtottariApplicable = Boolean(
    rahuPl && lagnaLordPl && [1, 4, 5, 7, 9, 10].includes(((rahuPl.house - lagnaLordPl.house + 12) % 12) + 1)
  );

  // 7 dense pages covering all 7 dasha systems (Pages 21 to 27)
  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 21: VIMSHOTTARI MAHADASHA 120-YEAR MASTER SEQUENCE & OPERATIVE STATUS*/}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ४ • विंशोत्तरी महादशा चक्र' : 'Grantha 4 • Vimshottari Mahadasha'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २१' : 'Page 21'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? '१२० वर्षीय विंशोत्तरी महादशा चक्र एवं कालगणना' : '120-YEAR VIMSHOTTARI MAHADASHA MASTER SEQUENCE'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              Lifetime chronometric progression mapping start/end dates, total span, operative status and transit convergence.
            </p>
          </div>

          <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={`${tableHeaderBg} font-bold text-xs`}>
                  <th className="p-2">Mahadasha</th>
                  <th className="p-2 font-mono">Duration</th>
                  <th className="p-2 font-mono">Start Date</th>
                  <th className="p-2 font-mono">End Date</th>
                  <th className="p-2">Operative Status</th>
                  <th className="p-2 text-astro-primary font-bold">Karmic Focus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dashas.map((d, idx) => {
                  const focuses = [
                    'Spiritual awakening, foreign travels, detachment',
                    'Aesthetic prosperity, conjugal harmony, conveyances',
                    'Executive authority, paternal recognition, vitality',
                    'Psychological sensitivity, public elevation, commerce',
                    'Courageous initiative, land assets, energetic trials',
                    'Radical expansion, disruptive tech, worldly breakthrough',
                    'Dharmic wisdom, progeny, higher knowledge, wealth',
                    'Discipline, organizational zenith, structural foundations',
                    'Commercial negotiations, intellect, analytical ventures'
                  ];
                  return (
                    <tr key={idx} className={d.isCurrent ? 'bg-amber-50 font-bold border-l-4 border-amber-600' : idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                      <td className="p-2 font-bold text-astro-primary">{getPlanetFullName(d.planet, lang)}</td>
                      <td className="p-2 font-mono">{VIMSHOTTARI_YEARS[d.planet] || 7} Years</td>
                      <td className="p-2 font-mono">{d.startDate ? new Date(d.startDate).toLocaleDateString() : '—'}</td>
                      <td className="p-2 font-mono">{d.endDate ? new Date(d.endDate).toLocaleDateString() : '—'}</td>
                      <td className="p-2 text-[11px] font-semibold">
                        {d.isCurrent ? <span className="text-emerald-700">● Operative Now</span> : new Date(d.endDate).getTime() < Date.now() ? <span className="text-gray-400">Past Completed</span> : <span className="text-indigo-600">Upcoming</span>}
                      </td>
                      <td className="p-2 text-[11px] text-gray-700 italic">{focuses[idx % focuses.length]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={`rounded-xl border p-3.5 text-xs space-y-1.5 ${innerCardBg}`}>
            <span className={`font-bold font-serif uppercase tracking-wider block ${headingPrimary}`}>
              {isHi ? 'विंशोत्तरी फलकथन सूत्र:' : 'Vimshottari Dasha Applied Interpretive Canons:'}
            </span>
            <p className="text-[11px] text-gray-700 leading-relaxed">
              Vimshottari dasha acts as the primary time-engine of Parashari Jyotish. Event fruition requires tri-factor alignment: (1) Operative Mahadasha & Antardasha lords, (2) Respective natal house lordship & placements, and (3) Simultaneous Gochar (transit) of Jupiter and Saturn over active natal cusps.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ४ • पृष्ठ २१' : 'Grantha 4 • Page 21')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 22 TO 24: ALL 9 MAHADASHA SUB-PERIODS (3 DENSE PAGES, 3 PER PAGE)   */}
      {/* ========================================================================= */}
      {[
        { list: ['Ketu', 'Venus', 'Sun'], page: 22, pageHi: '२२', title: 'Ketu, Venus & Sun Sub-Period Matrices' },
        { list: ['Moon', 'Mars', 'Rahu'], page: 23, pageHi: '२३', title: 'Moon, Mars & Rahu Sub-Period Matrices' },
        { list: ['Jupiter', 'Saturn', 'Mercury'], page: 24, pageHi: '२४', title: 'Jupiter, Saturn & Mercury Sub-Period Matrices' }
      ].map((subGroup, sIdx) => (
        <div key={sIdx} className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
          <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{subGroup.title}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${subGroup.pageHi}` : `Page ${subGroup.page}`}</span>
          </div>

          <div className="space-y-3 my-auto">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {subGroup.title.toUpperCase()}
              </h2>
            </div>

            <div className="space-y-3">
              {subGroup.list.map((mName, mIdx) => {
                const mdPeriod = dashas.find(d => d.planet === mName);
                const actualAntardashas = mdPeriod?.antardashas || [];

                return (
                  <div key={mIdx} className={`rounded-xl border p-2.5 space-y-1.5 ${innerCardBg}`}>
                    <div className="flex items-center justify-between border-b pb-1">
                      <span className="font-bold text-xs text-astro-primary font-serif flex items-center gap-2">
                        {getPlanetFullName(mName as any, lang)} Mahadasha Antardashas ({VIMSHOTTARI_YEARS[mName as any] || 7} Years)
                        {mdPeriod?.isCurrent && (
                          <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-sans font-semibold">
                            ● Operative MD
                          </span>
                        )}
                      </span>
                      {mdPeriod && (
                        <span className="text-[10px] font-mono text-gray-500">
                          {mdPeriod.startDate ? new Date(mdPeriod.startDate).getFullYear() : '—'} – {mdPeriod.endDate ? new Date(mdPeriod.endDate).getFullYear() : '—'}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-9 gap-1.5 text-center text-[10px] font-mono">
                      {actualAntardashas.map((ad, pIdx) => {
                        const startYr = ad.startDate ? new Date(ad.startDate).getFullYear() : '';
                        const endYr = ad.endDate ? new Date(ad.endDate).getFullYear() : '';
                        const yrSpan = startYr === endYr ? `${startYr}` : `${startYr}–${endYr}`;
                        return (
                          <div 
                            key={pIdx} 
                            className={`p-1 rounded border ${ad.isCurrent ? 'bg-amber-100 border-amber-500 font-bold shadow-xs' : 'bg-white/70'}`}
                          >
                            <span className="block font-bold text-astro-primary">{ad.planet.slice(0, 3)}</span>
                            <span className="text-[9px] text-gray-600 block">{ad.durationYears}y</span>
                            <span className="text-[8px] text-gray-400 block truncate">{yrSpan}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {renderPageFooter(isHi ? `ग्रन्थ ४ • पृष्ठ ${subGroup.pageHi}` : `Grantha 4 • Page ${subGroup.page}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 25: SHODASHOTTARI & TRIBHAGI DASHA + MULTI-DASHA CONVERGENCE (FULL)  */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ४ • षोडशोत्तरी, त्रिभागी एवं दशा समन्वय' : 'Grantha 4 • Shodashottari, Tribhagi & Convergence'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २५' : 'Page 25'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              SHODASHOTTARI (116-YR) & TRIBHAGI (80-YR) PROGRESSION & CONVERGENCE
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              Dual comparative auxiliary systems validating timing accuracy for critical karmic milestones.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
            {/* Shodashottari */}
            <div className={`rounded-xl border p-3.5 space-y-2 ${tableRowEven}`}>
              <div className="border-b pb-1 flex items-center justify-between">
                <div>
                  <span className="font-bold text-astro-primary font-serif block text-sm">Shodashottari Dasha (116-Year Cycle)</span>
                  <span className="text-[10px] text-gray-600">Day Krishna Paksha / Night Shukla Paksha birth progression</span>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isShodashottariApplicable ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                  {isShodashottariApplicable ? '● Applicable' : '○ Secondary'}
                </span>
              </div>
              <div className="space-y-1 font-mono text-[11px]">
                {[
                  { p: 'Sun', y: '11 Years' },
                  { p: 'Mars', y: '12 Years' },
                  { p: 'Jupiter', y: '13 Years' },
                  { p: 'Saturn', y: '14 Years' },
                  { p: 'Ketu', y: '15 Years' },
                  { p: 'Moon', y: '16 Years' },
                  { p: 'Mercury', y: '17 Years' },
                  { p: 'Venus', y: '18 Years' }
                ].map((s, i) => (
                  <div key={i} className="flex justify-between border-b pb-0.5">
                    <span className="font-sans font-semibold text-gray-800">{s.p}</span>
                    <strong className="text-astro-primary">{s.y}</strong>
                  </div>
                ))}
              </div>
            </div>

            {/* Tribhagi */}
            <div className={`rounded-xl border p-3.5 space-y-2 ${tableRowEven}`}>
              <div className="border-b pb-1">
                <span className="font-bold text-astro-primary font-serif block text-sm">Tribhagi Dasha (80-Year Cycle)</span>
                <span className="text-[10px] text-gray-600">2/3 Compressed progression accelerating event manifestations</span>
              </div>
              <div className="space-y-1 font-mono text-[11px]">
                {[
                  { p: 'Ketu', y: '4y 8m' },
                  { p: 'Venus', y: '13y 4m' },
                  { p: 'Sun', y: '4y 0m' },
                  { p: 'Moon', y: '6y 8m' },
                  { p: 'Mars', y: '4y 8m' },
                  { p: 'Rahu', y: '12y 0m' },
                  { p: 'Jupiter', y: '10y 8m' },
                  { p: 'Saturn', y: '12y 8m' },
                  { p: 'Mercury', y: '11y 4m' }
                ].map((s, i) => (
                  <div key={i} className="flex justify-between border-b pb-0.5">
                    <span className="font-sans font-semibold text-gray-800">{s.p}</span>
                    <strong className="text-astro-primary">{s.y}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Multi-Dasha Convergence Windows */}
          <div className={`rounded-xl border p-4 space-y-2 ${innerCardBg}`}>
            <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
              {isHi ? 'त्रि-दशा समन्वय एवं कालखंड सम्पुष्टि (Multi-Dasha Convergence Principle)' : 'Multi-Dasha Convergence & Transition Windows'}
            </h3>
            <p className="text-xs text-gray-700 leading-relaxed">
              When Vimshottari, Shodashottari, and Tribhagi converge upon identical planetary lords or house axes, predictive events manifest with exceptionally high certainty. Divergence indicates subtle internal psychological evolution preceding outer worldly breakthroughs.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ४ • पृष्ठ २५' : 'Grantha 4 • Page 25')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 26: ASHTOTTARI & KAALCHAKRA DASHA + DEHA/JEEVA VITALITY (FULL)       */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ४ • अष्टोत्तरी, कालचक्र एवं देह-जीव' : 'Grantha 4 • Ashtottari & Kaalchakra'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २६' : 'Page 26'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1.5">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              ASHTOTTARI (108-YR) & KAALCHAKRA (86-YR) DASHA MATRICES
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              Canonical conditional dasha systems evaluated for Rahu/Lagna quadrant alignments and Deha-Jeeva vitality.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
            {/* Ashtottari */}
            <div className={`rounded-xl border p-3.5 space-y-2 ${tableRowEven}`}>
              <div className="border-b pb-1 flex items-center justify-between">
                <div>
                  <span className="font-bold text-astro-primary font-serif block text-sm">Ashtottari Dasha (108-Year Cycle)</span>
                  <span className="text-[10px] text-gray-600">8-Planet cycle omitting Ketu (Rahu in Kendra/Trikona from Lagna Lord)</span>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isAshtottariApplicable ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                  {isAshtottariApplicable ? '● Applicable' : '○ Conditional'}
                </span>
              </div>
              <div className="space-y-1 font-mono text-[11px]">
                {[
                  { p: 'Sun (Surya)', y: '6 Years' },
                  { p: 'Moon (Chandra)', y: '15 Years' },
                  { p: 'Mars (Mangal)', y: '8 Years' },
                  { p: 'Mercury (Budha)', y: '17 Years' },
                  { p: 'Saturn (Shani)', y: '10 Years' },
                  { p: 'Jupiter (Guru)', y: '19 Years' },
                  { p: 'Rahu', y: '12 Years' },
                  { p: 'Venus (Shukra)', y: '21 Years' }
                ].map((s, i) => (
                  <div key={i} className="flex justify-between border-b pb-0.5">
                    <span className="font-sans font-semibold text-gray-800">{s.p}</span>
                    <strong className="text-astro-primary">{s.y}</strong>
                  </div>
                ))}
              </div>
            </div>

            {/* Kaalchakra */}
            <div className={`rounded-xl border p-3.5 space-y-2 ${tableRowEven}`}>
              <div className="border-b pb-1">
                <span className="font-bold text-astro-primary font-serif block text-sm">Kaalchakra Dasha (86-Year Movement)</span>
                <span className="text-[10px] text-gray-600">Constellation-quarter Navamsha progression tracking soul destiny</span>
              </div>
              <div className="space-y-1 font-mono text-[11px]">
                {[
                  { r: 'Aries (Deha Sign)', y: '7 Years' },
                  { r: 'Taurus', y: '16 Years' },
                  { r: 'Gemini', y: '9 Years' },
                  { r: 'Cancer (Simhavalokana)', y: '21 Years' },
                  { r: 'Leo', y: '5 Years' },
                  { r: 'Virgo', y: '9 Years' },
                  { r: 'Libra', y: '16 Years' },
                  { r: 'Scorpio (Jeeva Sign)', y: '7 Years' }
                ].map((s, i) => (
                  <div key={i} className="flex justify-between border-b pb-0.5">
                    <span className="font-sans font-semibold text-gray-800">{s.r}</span>
                    <strong className="text-astro-primary">{s.y}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Deha & Jeeva Vitality Assessment */}
          <div className={`rounded-xl border p-4 space-y-2 ${innerCardBg}`}>
            <h3 className={`font-bold uppercase tracking-wider text-xs border-b pb-1 ${headingPrimary}`}>
              {isHi ? 'कालचक्र देह एवं जीव राशि परीक्षण (Deha & Jeeva Preservation)' : 'Kaalchakra Deha & Jeeva Vitality Evaluation'}
            </h3>
            <p className="text-xs text-gray-700 leading-relaxed">
              When natural malefics (Saturn, Mars, Rahu) transit across the Deha (body) or Jeeva (life-force) signs, the native must prioritize health preservation and avoid reckless physical ventures. Beneficial transits across Jeeva confer immense vitality, spiritual insight, and creative fulfillment.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ४ • पृष्ठ २६' : 'Grantha 4 • Page 26')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 27: YOGINI & JAIMINI CHARA DASHA + SAMHITA COLOPHON (FULL)           */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ४ • योगिनी एवं जैमिनी चर दशा' : 'Grantha 4 • Yogini & Jaimini Dasha'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २७' : 'Page 27'}</span>
        </div>

        <div className="space-y-3.5 my-auto">
          {/* Yogini Dasha */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                YOGINI DASHA (36-YEAR RECURRENT CYCLIC SYSTEM)
              </h2>
            </div>

            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} text-xs font-bold`}>
                    <th className="p-1.5">Yogini Name</th>
                    <th className="p-1.5">Governing Planet</th>
                    <th className="p-1.5 font-mono">Duration</th>
                    <th className="p-1.5">Karmic Quality & Manifestation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { n: 'Mangala', p: 'Moon', y: '1 Year', q: 'Auspicious peace, spiritual expansion' },
                    { n: 'Pingala', p: 'Sun', y: '2 Years', q: 'Heat, friction, political status, executive power' },
                    { n: 'Dhanya', p: 'Jupiter', y: '3 Years', q: 'Abundant wealth, academic learning, family grace' },
                    { n: 'Bhramari', p: 'Mars', y: '4 Years', q: 'Travel, restlessness, competitive courage' },
                    { n: 'Bhadrika', p: 'Mercury', y: '5 Years', q: 'Intellectual acclaim, commercial gains, diplomacy' },
                    { n: 'Ulka', p: 'Saturn', y: '6 Years', q: 'Discipline, delays, karmic testing, endurance' },
                    { n: 'Siddha', p: 'Venus', y: '7 Years', q: 'Fulfillment of desires, artistic luxury, conveyances' },
                    { n: 'Sankata', p: 'Rahu', y: '8 Years', q: 'Intense trials, radical transformation, breakthroughs' }
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                      <td className="p-1.5 font-bold text-astro-primary">{row.n}</td>
                      <td className="p-1.5 font-semibold">{row.p}</td>
                      <td className="p-1.5 font-mono font-bold text-amber-900">{row.y}</td>
                      <td className="p-1.5 text-gray-700 text-[11px]">{row.q}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Jaimini Chara Dasha */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                JAIMINI CHARA DASHA SIGN-BASED PROGRESSION
              </h2>
            </div>

            <div className={`rounded-xl border p-3.5 space-y-1.5 ${innerCardBg}`}>
              <p className="text-xs text-gray-700 leading-relaxed">
                Jaimini Chara Dasha progresses through zodiac signs rather than planets. Operative signs aspecting Atmakaraka (AK) or Amatyakaraka (AmK) mark periods of monumental personal zenith and career elevation.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-[11px]">
                <div className="p-1.5 rounded border bg-white/70">
                  <span className="font-bold text-astro-primary block">Atmakaraka (AK):</span>
                  <span className="font-mono text-gray-800 font-bold">
                    {charaKarakas.find(k => k.code === 'AK')?.planet || 'Sun'}
                  </span>
                  <span className="text-[9px] text-gray-500 block">Soul Purpose Ruler</span>
                </div>
                <div className="p-1.5 rounded border bg-white/70">
                  <span className="font-bold text-astro-primary block">Amatyakaraka (AmK):</span>
                  <span className="font-mono text-gray-800 font-bold">
                    {charaKarakas.find(k => k.code === 'AmK')?.planet || 'Moon'}
                  </span>
                  <span className="text-[9px] text-gray-500 block">Career & Status Pillar</span>
                </div>
                <div className="p-1.5 rounded border bg-white/70">
                  <span className="font-bold text-astro-primary block">Bhratrikaraka (BK):</span>
                  <span className="font-mono text-gray-800 font-bold">
                    {charaKarakas.find(k => k.code === 'BK')?.planet || 'Mars'}
                  </span>
                  <span className="text-[9px] text-gray-500 block">Courage & Preceptors</span>
                </div>
                <div className="p-1.5 rounded border bg-white/70">
                  <span className="font-bold text-astro-primary block">Darakaraka (DK):</span>
                  <span className="font-mono text-gray-800 font-bold">
                    {charaKarakas.find(k => k.code === 'DK')?.planet || 'Venus'}
                  </span>
                  <span className="text-[9px] text-gray-500 block">Spouse & Partnerships</span>
                </div>
              </div>

              {charaDasha && (
                <div className="mt-2 p-2 rounded border bg-amber-50/70 border-amber-300 flex items-center justify-between text-xs">
                  <span className="font-bold text-amber-900 font-serif">
                    {isHi ? 'वर्तमान सक्रिय चर दशा राशि:' : 'Operative Chara Dasha Sign:'}
                  </span>
                  <span className="font-bold font-mono px-2 py-0.5 bg-amber-200 text-amber-900 rounded">
                    {charaDasha.activeSign}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ४ • पृष्ठ २७' : 'Grantha 4 • Page 27')}
      </div>
    </>
  );
};
